import { useState, type FormEvent } from 'react';
import { auth, ApiClientError } from '../lib/api';
import { useAuth } from '../hooks/useAuth';
import { formatDate } from '../lib/format';
import { Alert, Badge, Card } from '../components/ui';

export function Settings() {
  const { account } = useAuth();

  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  async function changePassword(event: FormEvent) {
    event.preventDefault();
    setError(null);
    setDone(false);
    setSubmitting(true);

    try {
      await auth.changePassword(current, next);
      setCurrent('');
      setNext('');
      setDone(true);
    } catch (caught) {
      setError(caught instanceof ApiClientError ? caught.message : 'Could not change the password');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <>
      <div className="page-header">
        <h1>Settings</h1>
        <p>Account details and security.</p>
      </div>

      <div style={{ display: 'grid', gap: 16, maxWidth: 560 }}>
        <Card title="Account">
          <div className="card-body">
            <dl style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '8px 20px', margin: 0 }}>
              <dt className="muted">Email</dt>
              <dd style={{ margin: 0 }}>
                {account?.email}{' '}
                {account?.email_verified ? (
                  <Badge tone="success">verified</Badge>
                ) : (
                  <Badge tone="warning">unverified</Badge>
                )}
              </dd>

              <dt className="muted">Name</dt>
              <dd style={{ margin: 0 }}>{account?.name ?? '—'}</dd>

              <dt className="muted">Company</dt>
              <dd style={{ margin: 0 }}>{account?.company ?? '—'}</dd>

              <dt className="muted">Role</dt>
              <dd style={{ margin: 0 }}>{account?.role}</dd>

              <dt className="muted">Member since</dt>
              <dd style={{ margin: 0 }}>{formatDate(account?.created_at)}</dd>
            </dl>
          </div>
        </Card>

        <Card title="Change password">
          <form className="card-body" onSubmit={changePassword}>
            {error && <Alert>{error}</Alert>}
            {done && <Alert tone="success">Password updated.</Alert>}

            <label className="field">
              <span>Current password</span>
              <input
                type="password"
                value={current}
                onChange={(event) => setCurrent(event.target.value)}
                autoComplete="current-password"
                required
              />
            </label>

            <label className="field">
              <span>New password</span>
              <input
                type="password"
                value={next}
                onChange={(event) => setNext(event.target.value)}
                autoComplete="new-password"
                minLength={12}
                required
              />
            </label>

            <button className="btn btn-primary" disabled={submitting}>
              {submitting ? 'Updating…' : 'Update password'}
            </button>
          </form>
        </Card>
      </div>
    </>
  );
}
